import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ArrowRight, ChevronLeft, ChevronRight, X, MapPin, CheckCircle, Image as ImageIcon, MessageCircle, Loader2 } from "lucide-react";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";
import { NotFoundView } from "@/pages/NotFoundPage";
import { usePublicData } from "@/hooks/usePublicData";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";
import { WHATSAPP_LINK, SITE_ORIGIN, getImageUrl, DEFAULT_PHONE } from "@/lib/publicSite";

const API = `${window.location.origin}/api`;
const AREAS = ["Abergavenny", "Monmouth", "Usk & Raglan", "Crickhowell", "Brecon", "Hereford", "Newport & Cwmbran", "Chepstow"];

// Phase 6A: provable legacy service slugs → current published slugs (verified
// against the production sitemap + public services endpoint, Aug 2026).
// Unknown slugs (no provable mapping) render the public noindex not-found view.
export const LEGACY_SERVICE_REDIRECTS = {
  "carpentry": "bespoke-carpentry",
  "general-repairs": "repairs-property-maintenance",
};

export default function ServicePage() {
  const { slug } = useParams();
  const { bizInfo, services } = usePublicData();
  const [service, setService] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(-1);

  useEffect(() => {
    let on = true;
    if (LEGACY_SERVICE_REDIRECTS[slug]) return undefined; // redirecting — no fetch
    setLoading(true);
    setNotFound(false);
    setService(null);
    axios.get(`${API}/site/services/slug/${slug}`).then((res) => {
      if (!on) return;
      setService(res.data?.service || null);
      setRelated(res.data?.related || []);
    }).catch(() => { if (on) setNotFound(true); }).finally(() => { if (on) setLoading(false); });
    window.scrollTo(0, 0);
    return () => { on = false; };
  }, [slug]);

  // Page view tracking
  useEffect(() => {
    try {
      let sessionId = sessionStorage.getItem('shs_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('shs_session_id', sessionId);
      }
      axios.post(`${API}/analytics/pageview`, null, {
        params: { page: `/services/${slug}`, referrer: document.referrer || null, session_id: sessionId },
      }).catch(() => {});
    } catch { /* ignore */ }
  }, [slug]);

  const photos = [...(service?.image_url ? [service.image_url] : []), ...(service?.photos || [])]
    .filter((u, i, a) => u && a.indexOf(u) === i);

  const nextImage = () => setLightbox((p) => (p + 1) % photos.length);
  const prevImage = () => setLightbox((p) => (p - 1 + photos.length) % photos.length);

  const metaDesc = service?.meta_description
    || `${service?.title || "Handyman services"} in Abergavenny & Monmouthshire by Sustainable Handyman Services. Eco-friendly, fully insured, free quotes — call ${bizInfo?.business_phone || DEFAULT_PHONE}.`;

  const jsonLd = service ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "serviceType": service.title,
    "description": metaDesc,
    "url": `${SITE_ORIGIN}/services/${service.slug}`,
    "areaServed": [
      { "@type": "City", "name": "Abergavenny" },
      { "@type": "AdministrativeArea", "name": "Monmouthshire" },
    ],
    "provider": {
      "@type": "LocalBusiness",
      "@id": `${SITE_ORIGIN}/#localbusiness`,
      "name": "Sustainable Handyman Services",
      "telephone": bizInfo?.business_phone || "+44 7359 139941",
    },
  } : null;

  // Head tags — called unconditionally so hook order stays stable across
  // loading / not-found / redirect states.
  useSeoHeadTags({
    title: notFound
      ? "Service not found | Sustainable Handyman Services"
      : service
        ? `${service.title} Abergavenny & Monmouthshire | Sustainable Handyman Services`
        : "Loading service | Sustainable Handyman Services",
    description: notFound ? "This service may have been renamed or removed." : metaDesc,
    canonical: service ? `${SITE_ORIGIN}/services/${service.slug}` : null,
    ogImage: photos[0] ? getImageUrl(photos[0]) : null,
    ogType: "website",
    noindex: notFound,
    jsonLd,
  });

  // Permanent client-side redirect for provable legacy slugs. The SPA host
  // cannot emit an HTTP 301 (documented limitation) — history is REPLACED so
  // the legacy URL never remains in the back stack.
  if (LEGACY_SERVICE_REDIRECTS[slug]) {
    return <Navigate to={`/services/${LEGACY_SERVICE_REDIRECTS[slug]}`} replace />;
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <PublicNav />
        <div data-testid="service-not-found">
          <NotFoundView
            heading="Service not found"
            message="This service may have been renamed or removed. Browse all our current services from the homepage."
          />
        </div>
        <PublicFooter bizInfo={bizInfo} services={services} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {loading ? (
        <div className="pt-40 pb-32 flex items-center justify-center text-muted-foreground gap-2" data-testid="service-page-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : service && (
        <>
          {/* Breadcrumb */}
          <section className="pt-20 sm:pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-xs text-muted-foreground" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary">Home</Link>
              <span className="mx-1">/</span>
              <Link to="/#services" className="hover:text-primary">Services</Link>
              <span className="mx-1">/</span>
              <span className="text-foreground">{service.title}</span>
            </nav>
          </section>

          {/* Hero */}
          <section className="pt-6 pb-12 sm:pb-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div className="space-y-4 sm:space-y-6">
                  <p className="text-accent font-medium tracking-wide uppercase text-xs sm:text-sm">Our Services</p>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-heading" data-testid="service-page-title">
                    {service.title} <span className="text-primary">in Abergavenny</span>
                  </h1>
                  <div className="text-base sm:text-lg text-muted-foreground leading-relaxed prose prose-sm max-w-none" data-testid="service-page-description" dangerouslySetInnerHTML={{ __html: service.description || "" }} />
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-accent" /> Eco-friendly materials
                    <CheckCircle className="h-4 w-4 text-accent ml-2" /> Fully insured
                    <CheckCircle className="h-4 w-4 text-accent ml-2" /> Free quotes
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
                    <Link to={`/enquiry?service=${encodeURIComponent(service.title)}`} data-testid="service-page-enquiry-btn" className="w-full sm:w-auto">
                      <Button size="lg" className="w-full sm:w-auto rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-medium">
                        Get a Free Quote
                        <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                      </Button>
                    </Link>
                    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="service-page-whatsapp-btn" className="w-full sm:w-auto">
                      <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-medium">
                        <MessageCircle className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                        WhatsApp Us
                      </Button>
                    </a>
                  </div>
                </div>
                {photos[0] && (
                  <div className="relative mt-6 lg:mt-0">
                    <div className="aspect-[4/3] rounded-2xl sm:rounded-3xl overflow-hidden bg-secondary cursor-pointer" onClick={() => setLightbox(0)}>
                      <img src={getImageUrl(photos[0])} alt={service.title} className="w-full h-full object-cover" data-testid="service-page-hero-image" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Long description */}
          {service.long_description && (
            <section className="py-10 sm:py-14 bg-secondary/30">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="prose prose-lg max-w-none text-muted-foreground leading-relaxed" data-testid="service-page-long-description" dangerouslySetInnerHTML={{ __html: service.long_description }} />
              </div>
            </section>
          )}

          {/* Photo gallery */}
          {photos.length > 1 && (
            <section className="py-12 sm:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-accent font-medium tracking-wide uppercase text-xs sm:text-sm mb-2">Our Work</p>
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight font-heading">{service.title} Photos</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4" data-testid="service-page-gallery">
                  {photos.map((url, idx) => (
                    <button
                      key={url}
                      type="button"
                      onClick={() => setLightbox(idx)}
                      className="group relative aspect-square rounded-lg sm:rounded-xl overflow-hidden cursor-pointer"
                      data-testid={`service-page-photo-${idx}`}
                    >
                      <img src={getImageUrl(url)} alt={`${service.title} ${idx + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </button>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Related gallery projects */}
          {related.length > 0 && (
            <section className="py-12 sm:py-16 bg-secondary/30">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-accent font-medium tracking-wide uppercase text-xs sm:text-sm mb-2">Recent Projects</p>
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight font-heading">Related {service.title} Projects</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {related.map((r) => {
                    const cover = r.photos?.[0];
                    return (
                      <Link key={r.id} to={r.slug ? `/gallery/${r.slug}` : "/gallery"} className="group" data-testid={`service-related-card-${r.slug || r.id}`}>
                        <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted mb-2">
                          {cover ? (
                            <img src={getImageUrl(cover)} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon className="h-8 w-8" /></div>
                          )}
                        </div>
                        <p className="font-medium group-hover:text-primary transition-colors">{r.title}</p>
                      </Link>
                    );
                  })}
                </div>
                <div className="text-center mt-6">
                  <Link to="/gallery" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium" data-testid="service-page-all-projects-link">
                    View all projects <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* Service area */}
          <section className="py-12 sm:py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-accent font-medium tracking-wide uppercase text-xs sm:text-sm mb-2">Where We Work</p>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight font-heading mb-4">
                {service.title} across Abergavenny & a 20-mile radius
              </h2>
              <div className="flex flex-wrap justify-center gap-2" data-testid="service-page-areas">
                {AREAS.map((a) => (
                  <span key={a} className="inline-flex items-center gap-1.5 bg-secondary text-secondary-foreground px-3 py-1.5 rounded-full text-sm">
                    <MapPin className="h-3.5 w-3.5 text-primary" /> {a}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="pb-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-secondary rounded-3xl p-8 md:p-12 text-center">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight font-heading mb-4">
                  Need {service.title.toLowerCase()}?
                </h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Send us a quick message and a few photos — we'll come back with a straight answer and a fair quote.
                </p>
                <Link to={`/enquiry?service=${encodeURIComponent(service.title)}`} data-testid="service-page-cta-btn">
                  <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg font-medium">
                    Request a Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* Lightbox */}
          <Dialog open={lightbox >= 0} onOpenChange={(o) => !o && setLightbox(-1)}>
            <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-0 bg-black/95 border-none overflow-hidden" data-testid="service-lightbox-dialog">
              <button onClick={() => setLightbox(-1)} className="absolute top-4 right-4 z-50 text-white/70 hover:text-white transition-colors" data-testid="service-lightbox-close">
                <X className="h-8 w-8" />
              </button>
              <div className="relative flex flex-col items-center justify-center min-h-[50vh] max-h-[90vh] overflow-auto p-4">
                {photos.length > 1 && (
                  <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 hover:bg-white/20 p-3 rounded-full text-white transition-colors" data-testid="service-lightbox-prev">
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                )}
                <div className="flex items-center justify-center px-16 py-8 overflow-auto">
                  {lightbox >= 0 && (
                    <img src={getImageUrl(photos[lightbox])} alt={service.title} className="max-w-full max-h-[75vh] object-contain rounded-lg" style={{ minHeight: '200px' }} />
                  )}
                </div>
                {photos.length > 1 && (
                  <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 hover:bg-white/20 p-3 rounded-full text-white transition-colors" data-testid="service-lightbox-next">
                    <ChevronRight className="h-6 w-6" />
                  </button>
                )}
                <div className="mt-4 text-center pb-2">
                  <p className="text-white/60 text-sm">{lightbox + 1} / {photos.length}</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      <PublicFooter bizInfo={bizInfo} services={services} />
    </div>
  );
}
